import { useCallback, useMemo, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { matchesAnySearch } from "@racoon/lib";
import {
  ALL_LOCATION_TYPES,
  useListDisposalSitesQuery,
  useListLocationsQuery,
} from "~/shared/api/locations/locations.grpc";
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation,
} from "~/shared/api/organization/organization.grpc";
import { LOCATION_TABS, useDepotUrl, type LocationTab } from "./useDepotUrl";

export const useDepotSettings = (selectedCompanyId?: number) => {
  const { locationTab, setLocationTab } = useDepotUrl();
  const [search, setSearch] = useState("");

  // Every kind of location the company owns (depot, disposal site, sorting
  // complex, …); split by status client-side to keep the counts in sync.
  const { data: { locations = [] } = {}, isLoading: isLocalLoading } = useListLocationsQuery(
    {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      types: ALL_LOCATION_TYPES,
    },
    { skip: !selectedCompanyId },
  );

  // Disposal sites of every operator near the caller's pickup locations.
  const { data: { locations: disposalSites = [] } = {}, isLoading: isGlobalLoading } =
    useListDisposalSitesQuery({ nearbyOnly: true });

  // Settings resolve to the current user's own org (gRPC getSetting, auth context).
  const {
    data: settings,
    isLoading: isSettingsLoading,
    isFetching: isSettingsFetching,
  } = useGetOrganizationSettingsQuery({});
  const [updateSettings, { isLoading: isActivating }] = useUpdateOrganizationSettingsMutation();

  const activeOperatorIds = useMemo(
    () => new Set((settings?.disposalSiteOrgIds?.ids ?? []).map(String)),
    [settings],
  );

  // The selection is stored per operator, so this flips every site it owns.
  const toggleOperator = useCallback(
    (operatorId: bigint) => {
      if (!settings) return;
      const ids = activeOperatorIds.has(String(operatorId))
        ? (settings.disposalSiteOrgIds?.ids ?? []).filter((id) => id !== operatorId)
        : [...(settings.disposalSiteOrgIds?.ids ?? []), operatorId];
      // A failure is toasted by GlobalErrorToaster.
      void updateSettings({ id: settings.id, disposalSiteOrgIds: { ids } });
    },
    [settings, activeOperatorIds, updateSettings],
  );

  const byTab = useMemo<Record<LocationTab, ConnectProto.Location[]>>(
    () => ({
      global: disposalSites,
      local: locations.filter((l) => l.status === ConnectProto.LocationStatus.ACTIVE),
      archived: locations.filter((l) => l.status === ConnectProto.LocationStatus.INACTIVE),
    }),
    [disposalSites, locations],
  );

  const counts = useMemo(
    () =>
      Object.fromEntries(LOCATION_TABS.map((tab) => [tab, byTab[tab].length])) as Record<
        LocationTab,
        number
      >,
    [byTab],
  );

  // Always a fresh array, rebuilt on activation too: DataTable caches cell values
  // per row until `data` changes, and RTK keeps the same reference for equal results.
  const rows = useMemo(() => {
    return byTab[locationTab].filter((l) => matchesAnySearch([l.name, l.address], search));
  }, [byTab, locationTab, search, activeOperatorIds]);

  return {
    rows,
    counts,
    locationTab,
    setLocationTab,
    search,
    setSearch,
    isLoading: locationTab === "global" ? isGlobalLoading || isSettingsLoading : isLocalLoading,
    activeOperatorIds,
    toggleOperator,
    // Each toggle sends the whole list built from `settings`, so wait for the
    // refetch after the previous one too — otherwise a fast second click drops it.
    isActivating: isActivating || isSettingsFetching,
  };
};
